import { DataFilter } from '@core/datafilter/datafilter';
import { DataFilterOperator } from '@core/datafilter/filter-operator';
import { toDateStringDE, toDateTimeStringDE } from '@core/functions/to-date-string';
import { deepEqual } from '@shared/functions/deep-equal';
import { isValidDate } from '@core/functions/is-valid-date';
import { getNestedAttribute } from '@core/functions/get-nested-attribute';

function equal(fieldValue, conditionValue) {
    if (!isValidDate(fieldValue) && !isValidDate(conditionValue) && deepEqual(fieldValue, conditionValue)) {
        return true;
    }

    const fieldValueDate = toDateStringDE(fieldValue);
    const conditionValueDate = toDateStringDE(conditionValue);

    if (fieldValueDate != null
        && conditionValueDate != null
        && fieldValueDate === conditionValueDate) {
        return true;
    }

    const fieldValueDateTime = toDateTimeStringDE(fieldValue);
    const conditionValueDateTime = toDateTimeStringDE(conditionValue);

    if (fieldValueDateTime === null && conditionValueDateTime === null) {
        return createLowerCasedString(fieldValue) != null
            && createLowerCasedString(conditionValue) != null
            && createLowerCasedString(fieldValue) === createLowerCasedString(conditionValue) ;
    }

    return fieldValueDateTime != null
        && conditionValueDateTime != null
        && fieldValueDateTime === conditionValue;
}

function contains(fieldValue: unknown | unknown[], conditionValue: unknown, attributeToCompare: string | [string, string]) {

    if (Array.isArray(fieldValue)) {
        if (!attributeToCompare) {
            return createLowerCasedString(fieldValue).indexOf(createLowerCasedString(conditionValue)) >= 0;
        }
        if (Array.isArray(attributeToCompare)) {
            return fieldValue.find(item => getNestedAttribute(item, attributeToCompare[0]) === getNestedAttribute(conditionValue, attributeToCompare[1])) !== undefined;
        }
        return fieldValue.find(item => getNestedAttribute(item, attributeToCompare) === getNestedAttribute(conditionValue, attributeToCompare)) !== undefined;
    }

    // using to lower case here, to always match Uppercase and lowercase searchtearm to conditionValue value
    return createLowerCasedString(fieldValue).indexOf(createLowerCasedString(conditionValue)) >= 0;
}

export function defineDefaultOperators<Entity>(dataFilter: DataFilter<Entity>): void {

    dataFilter.operators.add(
        DataFilterOperator.GreaterThan,
        (fieldValue, conditionValue) => (fieldValue > conditionValue)
    );

    dataFilter.operators.add(
        DataFilterOperator.GreaterThanOrEqual,
        (fieldValue, conditionValue) => {
            return (fieldValue >= conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.LessThan,
        (fieldValue, conditionValue) => {
            return (fieldValue < conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.LessThanOrEqual,
        (fieldValue, conditionValue) => {
            return (fieldValue <= conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.Equal,
        equal
    );

    dataFilter.operators.add(
        DataFilterOperator.NotEqual,
        (fieldValue, conditionValue) => {
            return !equal(fieldValue, conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.Contains,
        (fieldValue, conditionValue, attributeToCompare: string | [string, string]) => {
            return contains(fieldValue, conditionValue, attributeToCompare);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.NotContains,
        (fieldValue, conditionValue, attributeToCompare: string | [string, string]) => {
            return !contains(fieldValue, conditionValue, attributeToCompare);
        }
    );

    // dataFilter.operators.add(
    //     DataFilterOperator.Has,
    //     (fieldValue, conditionValue) => {
    //         return (Array.isArray(fieldValue) && fieldValue.indexOf(conditionValue) >= 0);
    //     }
    // );
    //
    // dataFilter.operators.add(
    //     DataFilterOperator.Matches,
    //     (fieldValue, conditionValue) => {
    //         if (typeof conditionValue === 'string') {
    //             conditionValue = new RegExp(conditionValue);
    //         }
    //
    //         return (typeof conditionValue.test === 'function' && conditionValue.test(fieldValue));
    //     }
    // );

    dataFilter.operators.add(
        DataFilterOperator.StartsWith,
        (fieldValue, conditionValue) => {
            fieldValue = createLowerCasedString(fieldValue);
            conditionValue = createLowerCasedString(conditionValue);
            return fieldValue.startsWith(conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.EndsWith,
        (fieldValue, conditionValue) => {
            fieldValue = createLowerCasedString(fieldValue);
            conditionValue = createLowerCasedString(conditionValue);
            return fieldValue.endsWith(conditionValue);
        }
    );

    dataFilter.operators.add(
        DataFilterOperator.IsEmpty,
        (fieldValue: string, conditionValue: string) => {
            fieldValue = createLowerCasedString(fieldValue);
            conditionValue = createLowerCasedString(conditionValue);

            const valuesAreEmptyStrings = fieldValue === '' && conditionValue === '';
            const valuesAreNullish = fieldValue === null && conditionValue === null;
            const valuesAreUndefined = fieldValue === undefined && conditionValue === undefined;

            return valuesAreEmptyStrings || valuesAreNullish || valuesAreUndefined;
        }
    );
}

function createLowerCasedString(value: any) {
    return String(value || '').toLowerCase();
}
